import env from "@/common/env"
import { fabric } from "fabric"
import { cloneDeep } from "lodash-es"
import { isMobile } from "@/common/util/misc"

const controlImagePaths: StringMap<string> = {
  rotate: "/images/signomatic/bounding_box/rotate-object-2.svg",
  move: "/images/signomatic/bounding_box/move.svg",
}

const controlImageMap: StringMap<string> = {
  mtr: "rotate",
  move: "move",
}

const controlImages: ImageMap = {}

export const loadControlImages = () => {
  if (env.isJSDOM) {
    return
  }

  for (const key in controlImagePaths) {
    if (Object.prototype.hasOwnProperty.call(controlImagePaths, key)) {
      const path = controlImagePaths[key]

      fabric.util.loadImage(path, img => {
        controlImages[key] = img
      })
    }
  }
}

const getControlImage = (control: string): HTMLImageElement | null => {
  const key = controlImageMap[control]

  if (key) {
    return controlImages[key]
  }

  return null
}

const controlsThreshold = (objectType: string, canvasZoom: number) => {
  if (!canvasZoom) {
    return 1
  }

  if (isMobile()) {
    if (objectType === "i-text") {
      if (canvasZoom > 1.9) return 17
      if (canvasZoom > 0.71) return 33
      if (canvasZoom > 0.38) return 55
      if (canvasZoom > 0.25) return 110
      if (canvasZoom > 0.175) return 145
      if (canvasZoom > 0.165) return 150
      if (canvasZoom > 0.16) return 200
      if (canvasZoom > 0.12) return 280
      if (canvasZoom > 0.08) return 355
      if (canvasZoom > 0.06) return 480
      return 920
    } else {
      if (canvasZoom > 1.9) return 60
      if (canvasZoom > 0.6) return 160
      if (canvasZoom > 0.25) return 180
      if (canvasZoom > 0.175) return 200
      if (canvasZoom > 0.165) return 210
      if (canvasZoom > 0.15) return 220
      if (canvasZoom > 0.1) return 230
      return 240
    }
  } else {
    if (objectType === "i-text") {
      if (canvasZoom > 3) return 6
      if (canvasZoom > 1.9) return 10
      if (canvasZoom > 0.71) return 20
      if (canvasZoom > 0.6) return 30
      if (canvasZoom > 0.38) return 50
      if (canvasZoom > 0.25) return 70
      if (canvasZoom > 0.175) return 110
      if (canvasZoom > 0.165) return 130
      if (canvasZoom > 0.16) return 140
      return 150
    } else {
      if (canvasZoom > 1.9) return 90
      if (canvasZoom > 0.6) return 92
      if (canvasZoom > 0.25) return 95
      if (canvasZoom > 0.175) return 100
      if (canvasZoom > 0.165) return 105
      if (canvasZoom > 0.145) return 110
      return 150
    }
  }
}

const controlImageMlThreshold = () => {
  return isMobile() ? 100 : 70
}

const hideControls = (objectWidth: number, objectHeight: number, threshold: number) => {
  return objectWidth < threshold || objectHeight < threshold
}

const controls = cloneDeep(new fabric.Text("").controls)

// https://github.com/fabricjs/fabric.js/blob/v460/src/controls.render.js#L20
const circleRender = function (
  this: fabric.Control,
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  styleOverride: any,
  fabricObject: fabric.Object
) {
  const showMl = !hideControls(fabricObject.width || 1, fabricObject.height || 1, controlImageMlThreshold())

  if (
    hideControls(
      fabricObject.width || 1,
      fabricObject.height || 1,
      controlsThreshold(fabricObject.type || "", fabricObject.canvas?.getZoom() || 1)
    )
  ) {
    if (fabricObject.type === "i-text") {
      fabricObject.setControlsVisibility({ bl: false, tl: false, tr: false })
    } else {
      fabricObject.setControlsVisibility({
        bl: false,
        tl: false,
        tr: false,
        mt: false,
        mr: false,
        mb: false,
        ml: showMl,
      })
    }
  } else {
    if (fabricObject.type === "i-text") {
      fabricObject.setControlsVisibility({ bl: true, tl: true, tr: true })
    } else {
      fabricObject.setControlsVisibility({
        bl: true,
        tl: true,
        tr: true,
        mt: true,
        mr: true,
        mb: true,
        ml: true,
      })
    }
  }
  styleOverride = styleOverride || {}
  const xSize = this.sizeX || styleOverride.cornerSize || fabricObject.cornerSize
  const ySize = this.sizeY || styleOverride.cornerSize || fabricObject.cornerSize
  const transparentCorners =
    typeof styleOverride.transparentCorners !== "undefined"
      ? styleOverride.transparentCorners
      : fabricObject.transparentCorners
  const methodName = transparentCorners ? "stroke" : "fill"
  const stroke = !transparentCorners && (styleOverride.cornerStrokeColor || fabricObject.cornerStrokeColor)
  let myLeft = left
  let myTop = top
  let size

  ctx.save()
  ctx.fillStyle = styleOverride.cornerColor || fabricObject.cornerColor
  ctx.strokeStyle = styleOverride.cornerStrokeColor || fabricObject.cornerStrokeColor

  if (xSize > ySize) {
    size = xSize
    ctx.scale(1.0, ySize / xSize)
    myTop = (top * xSize) / ySize
  } else if (ySize > xSize) {
    size = ySize
    ctx.scale(xSize / ySize, 1.0)
    myLeft = (left * ySize) / xSize
  } else {
    size = xSize
  }
  // custom border width
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(myLeft, myTop, size / 2, 0, 2 * Math.PI, false)
  ctx[methodName]()
  if (stroke) {
    ctx.stroke()
  }
  ctx.restore()
}

const drawBgCircle = function (left: number, top: number, ctx: CanvasRenderingContext2D) {
  ctx.beginPath()
  ctx.arc(left - 1, top - 1, 19, 0, Math.PI * 2)
  ctx.fillStyle = "#FFFFFF"
  ctx.fill()
  ctx.strokeStyle = "#E0E6EA"
  ctx.lineWidth = 1
  ctx.stroke()
}

const renderMoveControl = function (
  this: fabric.Control,
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  styleOverride: any,
  fabricObject: fabric.Object
) {
  if (
    !hideControls(
      fabricObject.width || 1,
      fabricObject.height || 1,
      controlsThreshold(fabricObject.type || "", fabricObject.canvas?.getZoom() || 1)
    )
  ) {
    return
  }

  const img = getControlImage("move")

  ctx.save()
  ctx.translate(left, top)
  ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle || 0))
  ctx.translate(-left, -top)

  drawBgCircle(left, top, ctx)

  if (img) {
    ctx.drawImage(img, left - 14, top - 14, 26, 26)
  } else {
    ctx.beginPath()
    ctx.arc(0, 0, 10, 0, Math.PI * 2)
    ctx.fill()
  }

  ctx.restore()
}

// https://github.com/fabricjs/fabric.js/blob/v5.3.1/src/control.class.js#L264
const calcCornerCoords = function (
  objectAngle: number,
  objectCornerSize: number,
  centerX: number,
  centerY: number,
  isTouch: boolean,
  fabricObject?: fabric.Object
) {
  const hideControl = hideControls(
    fabricObject?.width || 1,
    fabricObject?.height || 1,
    controlsThreshold(fabricObject?.type || "", fabricObject?.canvas?.getZoom() || 1)
  )

  if (!hideControl && this.actionName === "drag") {
    return {
      tl: { x: 0, y: 0 },
      tr: { x: 0, y: 0 },
      bl: { x: 0, y: 0 },
      br: { x: 0, y: 0 },
    }
  }

  const angleToRadians = fabric.util.degreesToRadians(objectAngle)
  let cosHalfOffset, sinHalfOffset, cosHalfOffsetComp, sinHalfOffsetComp
  const xSize = isTouch ? this.touchSizeX : this.sizeX
  const ySize = isTouch ? this.touchSizeY : this.sizeY

  if (xSize && ySize && xSize !== ySize) {
    const controlTriangleAngle = Math.atan2(ySize, xSize)
    const cornerHypotenuse = Math.sqrt(xSize * xSize + ySize * ySize) / 2
    const newTheta = controlTriangleAngle - angleToRadians
    const newThetaComp = Math.PI / 2 - controlTriangleAngle - angleToRadians
    cosHalfOffset = cornerHypotenuse * (fabric as any).util.cos(newTheta)
    sinHalfOffset = cornerHypotenuse * (fabric as any).util.sin(newTheta)
    cosHalfOffsetComp = cornerHypotenuse * (fabric as any).util.cos(newThetaComp)
    sinHalfOffsetComp = cornerHypotenuse * (fabric as any).util.sin(newThetaComp)
  } else {
    const cornerSize = xSize && ySize ? xSize : 30
    const cornerHypotenuse = cornerSize * 0.7071067812
    const newTheta = fabric.util.degreesToRadians(45 - objectAngle)
    cosHalfOffset = cosHalfOffsetComp = cornerHypotenuse * (fabric as any).util.cos(newTheta)
    sinHalfOffset = sinHalfOffsetComp = cornerHypotenuse * (fabric as any).util.sin(newTheta)
  }

  const angleOffset = {
    x: (fabric as any).util.cos(angleToRadians) * -26,
    y: (fabric as any).util.sin(angleToRadians) * -26,
  }

  const offset = hideControl && this.actionName === "rotate" ? angleOffset : { x: 0, y: 0 }
  const adjustedCenterX = centerX + offset.x
  const adjustedCenterY = centerY + offset.y

  return {
    tl: {
      x: adjustedCenterX - sinHalfOffsetComp,
      y: adjustedCenterY - cosHalfOffsetComp,
    },
    tr: {
      x: adjustedCenterX + cosHalfOffset,
      y: adjustedCenterY - sinHalfOffset,
    },
    bl: {
      x: adjustedCenterX - cosHalfOffset,
      y: adjustedCenterY + sinHalfOffset,
    },
    br: {
      x: adjustedCenterX + sinHalfOffsetComp,
      y: adjustedCenterY + cosHalfOffsetComp,
    },
  }
}

controls["tl"].render = circleRender
controls["tr"].render = circleRender
controls["bl"].render = circleRender
controls["br"].render = circleRender
controls["ml"].render = circleRender
controls["mr"].render = circleRender
controls["mt"].render = circleRender
controls["mb"].render = circleRender

controls["tl"].calcCornerCoords = calcCornerCoords
controls["tr"].calcCornerCoords = calcCornerCoords
controls["bl"].calcCornerCoords = calcCornerCoords
controls["br"].calcCornerCoords = calcCornerCoords
controls["ml"].calcCornerCoords = calcCornerCoords
controls["mr"].calcCornerCoords = calcCornerCoords
controls["mt"].calcCornerCoords = calcCornerCoords
controls["mb"].calcCornerCoords = calcCornerCoords

controls["mtr"].withConnection = false
controls["mtr"].y = 0.5
controls["mtr"].offsetY = 60
controls["mtr"].cursorStyle = "pointer"
controls["mtr"].calcCornerCoords = calcCornerCoords
controls["mtr"].render = function (this: fabric.Control, ctx, left, top, styleOverride, fabricObject) {
  const hideControl = hideControls(
    fabricObject.width || 1,
    fabricObject.height || 1,
    controlsThreshold(fabricObject.type || "", fabricObject.canvas?.getZoom() || 1)
  )
  const offsetX = hideControl ? -26 : 0
  const img = getControlImage("mtr")

  ctx.save()

  ctx.translate(left, top)
  ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle || 0))
  ctx.translate(-left, -top)

  drawBgCircle(left + offsetX, top, ctx)

  if (img) {
    ctx.drawImage(img, left + offsetX - 14, top - 14, 26, 26)
  } else {
    circleRender.bind(this)(ctx, left + offsetX, top, styleOverride, fabricObject)
  }

  ctx.restore()
}

controls["move"] = new fabric.Control({
  y: 0.5,
  offsetY: 60,
  offsetX: 26,
  cursorStyle: "pointer",
  actionHandler: (fabric as any).controlsUtils.dragHandler,
  actionName: "drag",
  render: renderMoveControl,
  calcCornerCoords: calcCornerCoords,
})

export default controls
